import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { CheckCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useAddPaymentConfig,
  useConnectPaymentMethod,
  useDecryptPspCredentials,
  useUpdatePSPConfig,
  usePSPConfig,
  useChoices,
} from "@features/ix/payment/hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";
import {
  FormService,
  FormConfig,
  FormField,
} from "@shared/services/FormServices";
import { PageHeader } from "@shared/components/PageHeader";

export const DokuConfigForm = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const CURRENCY = useCurrency();
  const { data: user } = useGlobalUserProfile();

  const { data: pspConfigList, refetch } = usePSPConfig(remoteUtilityId);
  const { data: environments } = useChoices("environment");
  const { data: versions } = useChoices("version");

  const dokuData = useMemo(() => {
    if (!pspConfigList) return null;
    const psp = pspConfigList.find(
      (p: any) => p?.organizationName?.toLowerCase() === "doku",
    );
    if (!psp) return null;
    return {
      id: psp.pspId,
      pspUtilityId: psp.pspUtilityId ?? null,
      title: psp.organizationName,
      description: psp.description ?? "",
      credentials: psp.pspCredential ?? {},
      version: psp.version ?? "",
      environment: psp.environment ?? "",
      apiEndpoint: psp.apiEndpoint ?? "",
      enabled: psp.isActive,
      verificationStatus: psp.verificationStatus ?? "",
    };
  }, [pspConfigList]);

  const [decrypt, setDecrypt] = useState(false);
  const [versionSelected, setVersion] = useState<string | undefined>();
  const pspUtilityId = dokuData?.pspUtilityId;

  const { mutate: updatePSPConfig } = useUpdatePSPConfig(pspUtilityId);
  const { mutate: addConfig } = useAddPaymentConfig();
  const { mutate: connectPaymentMethod } = useConnectPaymentMethod();
  const { data: decryptedCredentials } = useDecryptPspCredentials(
    pspUtilityId,
    decrypt,
  );

  const fields: FormField[] = useMemo(() => {
    return [
      {
        name: "environment",
        label: "Environment",
        type: "select",
        required: true,
        options: environments,
      },
      {
        name: "version",
        label: "Version",
        type: "select",
        required: true,
        options: versions,
      },
      {
        name: "mallId",
        label: "Mall ID",
        type: "text",
        required: true,
        placeholder: "Enter Mall ID",
      },
      {
        name: "chainMerchantId",
        label: "Chain Merchant ID",
        type: "text",
        placeholder: "Enter Chain Merchant ID",
        required: versionSelected === "1",
        showWhen: (formValues) => formValues.version === "1",
      },
      {
        name: "secretKey",
        label: "Secret Key",
        type: "text",
        required: versionSelected === "1",
        showWhen: (formValues) => formValues.version === "1",
        placeholder: "Enter Secret Key",
      },
      {
        name: "sharedKey",
        label: "Shared Key",
        type: "text",
        required: true,
        placeholder: "Enter Shared Key",
      },
      {
        name: "apiKey",
        label: "API Key",
        type: "text",
        required: versionSelected === "1",
        showWhen: (formValues) => formValues.version === "1",
        placeholder: "Enter API Key",
      },
      {
        name: "webhook_url",
        label: "Webhook URL",
        type: "text",
        required: false,
        placeholder: "https://api.smart360.io/webhooks/doku",
        disabled: true,
      },
    ];
  }, [environments, versions, versionSelected]);

  const normalizedCredentials = useMemo(() => {
    if (!dokuData) return {};
    return {
      ...dokuData.credentials,
      version: dokuData.version ?? "",
      environment: dokuData.environment ?? "",
      webhook_url: dokuData.apiEndpoint,
    };
  }, [dokuData]);

  const initialValues = useMemo(() => {
    return fields.reduce(
      (acc, field) => {
        acc[field.name] = normalizedCredentials[field.name] ?? "";
        return acc;
      },
      {} as Record<string, any>,
    );
  }, [fields, normalizedCredentials]);

  const formInstance = FormService.createForm({
    fields,
    mode: "onSubmit",
    validateOnMount: false,
  });

  useEffect(() => {
    if (!dokuData?.credentials) return;
    Object.entries(initialValues).forEach(([field, value]) =>
      formInstance.setValue(field, value as any),
    );
    // synchronize the local version state initially
    setVersion(initialValues.version || undefined);
  }, [formInstance, initialValues, dokuData, environments, versions]);

  const allFilled = fields.every(
    (field) =>
      !field.required ||
      (initialValues?.[field.name] &&
        initialValues[field.name].toString().trim() !== ""),
  );

  const handleTogglePayment = (checked: boolean) => {
    if (dokuData?.verificationStatus !== "Verified") {
      toast({
        title: "Connect the payment method first to activate it.",
      });
      return;
    }
    if (!pspUtilityId) {
      toast({
        title: "Error",
        description: "Please configure the payment details first",
        variant: "destructive",
      });
      return;
    }
    updatePSPConfig({ isActive: checked }, { onSuccess: refetch });
  };

  const handleSubmitSaveConfig = (data: any) => {
    const { webhook_url, version, environment, ...formValues } = data;

    let payload;
    switch (version) {
      case "0":
        payload = {
          remote_utility_id: Number(remoteUtilityId),
          currency: CURRENCY,
          psp: dokuData?.id,
          version: version,
          environment,
          psp_credentials: {
            mallId: formValues.mallId,
            sharedKey: formValues.sharedKey,
          },
        };
        break;

      case "1":
        payload = {
          remote_utility_id: Number(remoteUtilityId),
          currency: CURRENCY,
          psp: dokuData?.id,
          version: version,
          environment,
          psp_credentials: {
            mallId: formValues.mallId,
            secretKey: formValues.secretKey,
            apiKey: formValues.apiKey,
            sharedKey: formValues.sharedKey,
          },
        };
        break;

      default:
        return;
    }

    if (pspUtilityId && allFilled) {
      const editPayload = FormService.getUpdatedFields(initialValues, data);
      updatePSPConfig(
        { psp_credentials: { ...editPayload } },
        { onSuccess: refetch },
      );
    } else {
      addConfig(payload, { onSuccess: refetch });
    }
  };

  const handleConnect = () => {
    const baseUrl = import.meta.env.VITE_PAYMENT_API_ENDPOINT;
    const payload = {
      amount: 1,
      success_url: `${baseUrl}doku/redirect/`,
      cancel_url: `${baseUrl}doku/redirect/`,
      remote_consumer_id: user?.user?.id,
      psp_mapping_id: pspUtilityId,
      remote_utility_id: Number(remoteUtilityId),
      description: "It is a billing payment",
      payment_type: "TEST",
      name: user?.user?.name,
      email: user?.user?.email,
      phone: "9876543210",
      source: 0,
    };

    connectPaymentMethod(payload, {
      onSuccess: (response) => {
        // ---- DOKU Version 1 Handling ----
        if (response?.formData && response?.url) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = response.url;
          form.target = "_blank";
          form.style.display = "none";

          Object.entries(response.formData).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key; // force uppercase field names
            input.value = value as string;
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          return;
        }

        // ---- DOKU Version 2 Handling ----
        else if (response?.url) {
          window.open(response.url, "_blank", "noopener,noreferrer");
          return;
        }
      },
    });
  };

  const handleDecryptCredentials = () => {
    if (!decrypt) {
      setDecrypt(true);
    } else {
      Object.entries(initialValues).forEach(([key, value]) =>
        formInstance.setValue(key, value as any),
      );
      setDecrypt(false);
    }
  };

  useEffect(() => {
    if (decrypt && decryptedCredentials) {
      Object.entries(decryptedCredentials).forEach(([field, value]) => {
        formInstance.setValue(field, value as any);
      });
    }
  }, [decryptedCredentials, decrypt, formInstance]);

  const renderCustomFooter = () => (
    <div className="flex flex-col gap-6">
      {dokuData?.verificationStatus?.toLowerCase() === "verified" && (
        <Alert variant="default" className="bg-green-50">
          <CheckCircle className="h-5 w-5 !text-green-500" />
          <AlertTitle className="text-green-600">
            Gateway connected successfully
          </AlertTitle>
          <AlertDescription className="text-green-500">
            Your gateway is properly configured and ready to accept payments.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4">
        <Button
          type="submit"
          onClick={formInstance.handleSubmit(handleSubmitSaveConfig)}
        >
          {allFilled ? "Edit Configuration" : "Save Configuration"}
        </Button>

        {dokuData?.verificationStatus?.toLowerCase() === "in progress" && (
          <Button type="button" onClick={handleConnect}>
            Connect
          </Button>
        )}

        {dokuData?.verificationStatus?.toLowerCase() === "verified" && (
          <Button type="button" disabled>
            Connected
          </Button>
        )}

        {(dokuData?.verificationStatus?.toLowerCase() === "in progress" ||
          dokuData?.verificationStatus?.toLowerCase() === "verified") &&
          allFilled && (
            <Button type="button" onClick={handleDecryptCredentials}>
              {decrypt ? <EyeOff className="mr-2" /> : <Eye className="mr-2" />}
              {decrypt ? "Hide Keys" : "Decrypt Keys"}
            </Button>
          )}
      </div>
    </div>
  );

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "version") {
      setVersion(value);
    }
  };

  const config: FormConfig = {
    fields,
    showResetButton: false,
    showSubmitButton: false,
    resetOnSubmit: false,
    shouldUnregister: false,
    layout: "grid",
    columns: 2,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      error: "text-sm text-red-500 mt-1",
      buttonContainer: "hidden",
    },
  };

  if (!dokuData) {
    return (
      <div className="p-6 text-gray-500">Loading Doku configuration...</div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center pb-4 justify-between space-y-0">
        <div>
          <PageHeader title={dokuData.title} subtitle={dokuData.description} />
        </div>
        <Switch
          checked={dokuData.enabled}
          onCheckedChange={handleTogglePayment}
        />
      </CardHeader>
      <CardContent className="pt-8 border-t">
        <DynamicForm
          form={formInstance}
          fields={fields}
          config={config}
          gridColumns={2}
          renderCustomFooter={renderCustomFooter}
          onSubmit={handleSubmitSaveConfig}
          onFieldChange={handleFieldChange}
        />
      </CardContent>
    </Card>
  );
};
